import {
  isAnyDateField,
  isBoolField,
  isDecimalField,
  isIntegerField,
  isCurrencyField,
  isStringListField,
  isAnyOptionListField
} from "Utils/Metadata";
import { InternalFieldNames } from "Components/Overview/Grid/InternalFieldNames";
import { IProject } from "Model/Storage/IProject";
import { IExtendedColumn } from "../IExtendedColumn";
import { CheckboxRenderer, ICheckboxRendererProps } from "./CheckboxRenderer";
import { CloneRenderer } from "./CloneRenderer";
import { DataSourceRenderer } from "./DataSourceRenderer";
import { DateRenderer } from "./DateRenderer";
import { CurrencyRenderer } from "./CurrencyRenderer";
import { NameRenderer } from "./NameRenderer";
import { NumberRenderer } from "./NumberRenderer";
import { StringListRenderer } from "./StringListRenderer";
import { TextRenderer } from "./TextRenderer";
import { UrlRenderer } from "./UrlRenderer";
import { UserRenderer } from "./UserRenderer";
import { OptionListRenderer } from "./OptionListRenderer";

const INTERNAL_FIELD_LIST = Object.keys(InternalFieldNames).map((key) => InternalFieldNames[key]);

export function onRender(project?: IProject, index?: number, column?: IExtendedColumn): JSX.Element {
  if (project == null) {
    return null;
  }

  const { fieldName } = column;

  if (isInternalField(fieldName)) {
    return handleInternalField(column, project);
  }

  if (isCurrencyField(column)) {
    return <CurrencyRenderer {...column} item={project} />;
  }

  if (isAnyOptionListField(column)) {
    return <OptionListRenderer {...column} item={project} />;
  }

  if (isStringListField(column)) {
    return <StringListRenderer {...column} item={project} />;
  }

  if (isAnyDateField(column)) {
    return <DateRenderer {...column} item={project} />;
  }

  if (isIntegerField(column) || isDecimalField(column)) {
    return <NumberRenderer {...column} item={project} />;
  }

  if (isBoolField(column)) {
    return <CheckboxRenderer {...column} item={project} />;
  }

  return <TextRenderer {...column} item={project} />;
}

// INTERNAL_FIELD_HANDLING
function isInternalField(fieldName: string): boolean {
  return INTERNAL_FIELD_LIST.includes(fieldName);
}

function handleInternalField(column: IExtendedColumn, project: IProject): JSX.Element {
  const { fieldName } = column;

  if (isFlagField(fieldName)) {
    return getFlagField(column, project);
  }

  if (fieldName === InternalFieldNames.Name) {
    return <NameRenderer {...column} item={project} />;
  }

  if (fieldName === InternalFieldNames.UserName) {
    return <UserRenderer {...column} item={project} />;
  }

  if (fieldName === InternalFieldNames.DataSource) {
    return <DataSourceRenderer {...column} item={project} />;
  }

  if (fieldName === InternalFieldNames.TargetUrl) {
    return <UrlRenderer {...column} item={project} />;
  }

  if (fieldName === InternalFieldNames.OriginId) {
    return <CloneRenderer {...column} item={project} />;
  }

  if (fieldName === InternalFieldNames.RelevantCount) {
    return <NumberRenderer {...column} item={project} />;
  }

  return <TextRenderer {...column} item={project} />;
}

function isFlagField(fieldName: string): boolean {
  return INTERNAL_FIELD_LIST.filter((field) => field.startsWith(`@is`)).includes(fieldName);
}

function getFlagField(column: IExtendedColumn, project: IProject): JSX.Element {
  return <CheckboxRenderer {...column} {...getFlagFieldAdditionalProps(column)} item={project} />;
}

function getFlagFieldAdditionalProps(
  column: IExtendedColumn
): Omit<ICheckboxRendererProps, "name" | "item" | "minWidth" | "key" | "translator"> {
  const { fieldName } = column;

  switch (fieldName) {
    case InternalFieldNames.AddressValidation:
      return { dateFieldName: `@avLastCheckedOn` };
    case InternalFieldNames.DuplicateCheck:
      return { dateFieldName: `@dcLastCheckedOn` };
    case InternalFieldNames.Exported:
      return {
        dateFieldName: `@exportedOn`,
        urlFieldName: InternalFieldNames.TargetUrl
      };
    default:
      return {};
  }
}
